import type { ChangeEvent, RefObject } from "react";
import { Download, FileSpreadsheet, Loader2, Upload } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

import { EXCEL_ACCEPT, PRO014_PRODUCTS_TEXTS } from "../../constants";

interface ProductsExcelImportProps {
  fileInputRef: RefObject<HTMLInputElement>;
  uploadProgress: number | null;
  isProcessing: boolean;
  disabled: boolean;
  onDownloadTemplate: () => void;
  onFileUpload: (event: ChangeEvent<HTMLInputElement>) => Promise<void>;
}

export function ProductsExcelImport({
  fileInputRef,
  uploadProgress,
  isProcessing,
  disabled,
  onDownloadTemplate,
  onFileUpload,
}: ProductsExcelImportProps): JSX.Element {
  const importLabel: string =
    uploadProgress !== null
      ? PRO014_PRODUCTS_TEXTS.IMPORTING(uploadProgress)
      : PRO014_PRODUCTS_TEXTS.IMPORT_EXCEL;

  return (
    <div className="space-y-2 border-t pt-2">
      <Label className="text-sm font-medium">{PRO014_PRODUCTS_TEXTS.EXCEL_TITLE}</Label>
      <p className="text-xs text-muted-foreground">{PRO014_PRODUCTS_TEXTS.EXCEL_SUBTITLE}</p>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button type="button" variant="outline" onClick={onDownloadTemplate} className="h-9 flex-1">
          <Download className="mr-2 h-4 w-4" aria-hidden />
          {PRO014_PRODUCTS_TEXTS.DOWNLOAD_TEMPLATE}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={isProcessing || disabled}
          className="h-9 flex-1"
        >
          {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
          {!isProcessing && <Upload className="mr-2 h-4 w-4" aria-hidden />}
          {importLabel}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept={EXCEL_ACCEPT}
          onChange={(event: ChangeEvent<HTMLInputElement>) => void onFileUpload(event)}
          className="hidden"
        />
      </div>
      {uploadProgress !== null && (
        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
          <div
            className="h-full bg-primary transition-all duration-300 ease-out"
            style={{ width: `${uploadProgress}%` }}
          />
        </div>
      )}
      <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50 p-2">
        <div className="flex items-start gap-2">
          <FileSpreadsheet className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" aria-hidden />
          <p className="text-xs text-blue-900">
            <strong>{PRO014_PRODUCTS_TEXTS.TIP_LABEL}</strong> {PRO014_PRODUCTS_TEXTS.TIP}
          </p>
        </div>
      </div>
    </div>
  );
}
